import { AnyAction } from 'redux';
import {
  GlobalActions,
  TARIF_CHECKED,
  VereinbarungenChanged,
  VorbelegungPayload,
  VorversicherungChanged,
} from '@ps20-frontend/shop-integration-redux';
import cloneDeep from 'lodash/cloneDeep';
import {
  App,
  determineLaufzeit,
  getVetragsOptionenFromVorvertrag,
  RsVorbelegungdaten,
  VersicherterBereichEnum,
} from '@ps20-frontend/frontend-models';
import { FahrzeugArt, FahrzeugeVersichert, Selbstbeteiligung } from '@ps20/bff-models';
import { addVorvertragToAngeboteReducer, vertragsoptionenReducer } from '@ps20-frontend/reducers';
import { IAppState } from '../appState';
import { RsActions } from '../rsActions';
import { Fahrzeug, VertragsoptionenRS } from '../models/vertragsoptionen.model';
import { selectRechtsstreitigkeitenUeberschritten } from '../../app/shared/selectors/rechtsstreitRechtsberatung.selector';

export const rsVertragsoptionenReducer = function (
  sectionState: VertragsoptionenRS = null,
  action: AnyAction,
  state: IAppState,
): VertragsoptionenRS {
  sectionState = vertragsoptionenReducer(sectionState, action, App.RS, state) as VertragsoptionenRS;
  switch (action.type) {
    case RsActions.ANZAHL_RECHTSSTREITIGKEITEN_CHANGED: {
      if (selectRechtsstreitigkeitenUeberschritten) {
        return {
          ...sectionState,
          selbstbeteiligung: Selbstbeteiligung.SELBSTBETEILIGUNG_0150,
        };
      }
      return sectionState;
    }
    case GlobalActions.VORVERSICHERUNG_VORHANDEN_CHANGED: {
      const actionPayload: VorversicherungChanged = action.payload;
      if (actionPayload.appId === App.RS) {
        return {
          ...sectionState,
          vorversicherungVorhanden: actionPayload.vorhanden,
        };
      }
      return sectionState;
    }
    case TARIF_CHECKED: {
      const newState: VertragsoptionenRS = cloneDeep(sectionState);
      determineLaufzeit(action, newState, state.produkt, sectionState);
      return newState;
    }
    case RsActions.VERSICHERTER_BEREICH_CHANGED: {
      const versicherterBereich: VersicherterBereichEnum = action.payload;
      const fahrzeug: Fahrzeug =
        versicherterBereich === VersicherterBereichEnum.KOMBI
          ? null
          : {
              fahrzeugArt: FahrzeugArt.PKW,
              anzahlFahrzeug: FahrzeugeVersichert.EINS,
            };
      return { ...sectionState, versicherterBereich, fahrzeug };
    }
    case RsActions.ANZAHLFAHRZEUG_CHANGED: {
      const anzahlFahrzeug: FahrzeugeVersichert = action.payload;
      const fahrzeugArt =
        anzahlFahrzeug === FahrzeugeVersichert.EINS ? FahrzeugArt.PKW : FahrzeugArt.UNBEKANNT;
      return {
        ...sectionState,
        fahrzeug: {
          fahrzeugArt,
          anzahlFahrzeug,
        },
      };
    }
    case RsActions.FAHRZEUGART_CHANGED: {
      const fahrzeugArt: FahrzeugArt = action.payload;
      return {
        ...sectionState,
        fahrzeug: {
          ...sectionState.fahrzeug,
          fahrzeugArt,
        },
      };
    }
    case RsActions.SELBSTBETEILIGUNG_CHANGED: {
      const selbstbeteiligung: Selbstbeteiligung = action.payload;
      return {
        ...sectionState,
        selbstbeteiligung,
      };
    }
    case GlobalActions.ADD_VORVERTRAG_TO_ANGEBOTE: {
      return addVorvertragToAngeboteReducer(sectionState, action, App.RS) as VertragsoptionenRS;
    }
    case GlobalActions.VEREINBARUNGEN_CHANGED: {
      const newState: VertragsoptionenRS = cloneDeep(sectionState);
      const actionPayload: VereinbarungenChanged = action.payload;
      const kuendigungenPayload = actionPayload.kuendingungChanged.some(elem => {
        return elem.appId === App.RS;
      });
      if (kuendigungenPayload) {
        newState.vorvertragGekuendigt = true;
      }
      return newState;
    }
    case GlobalActions.VORBELEGUNG_FOR_BEIBEHALTUNG_REQUESTED: {
      const actionPayload: RsVorbelegungdaten = action.payload;
      if (actionPayload.forApp === App.RS) {
        const newState: VertragsoptionenRS = getVetragsOptionenFromVorvertrag(
          sectionState,
          actionPayload,
        ) as VertragsoptionenRS;
        if (actionPayload.verkaufsangebotKurzname === 'RS005') {
          newState.versicherterBereich = VersicherterBereichEnum.VERKEHR;
          newState.fahrzeug = {
            anzahlFahrzeug: actionPayload.fahrzeugAnzahl,
            fahrzeugArt: actionPayload.fahrzeugArt,
          };
        }
        return { ...newState };
      }
      return sectionState;
    }
    case GlobalActions.VORBELEGUNG_FINISHED: {
      const actionPayload: VorbelegungPayload = action.payload;
      const { appId } = actionPayload;
      if (appId === App.RS) {
        const rsVorbelegungdaten: RsVorbelegungdaten =
          actionPayload.vorhandeneVorbelegungdaten as RsVorbelegungdaten;
        const versicherterBereich = rsVorbelegungdaten?.kfzOnly === true
          ? VersicherterBereichEnum.VERKEHR
          : sectionState.versicherterBereich;

        return {
          ...sectionState,
          urbeginn: sectionState.beibehalten === true ? rsVorbelegungdaten?.urbeginn : null,
          versicherterBereich,
          fahrzeug: {
            fahrzeugArt: rsVorbelegungdaten.fahrzeugArt ?? FahrzeugArt.PKW,
            anzahlFahrzeug: rsVorbelegungdaten.fahrzeugAnzahl,
            amtlichesKennzeichen: rsVorbelegungdaten.kfzKennzeichen,
          },
        };
      }
      return sectionState;
    }
    default:
      return sectionState;
  }
};
